'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  Gem,
  Hammer,
  Loader2,
  Receipt,
  Shuffle,
  Sparkles,
  Swords,
  Wallet,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { fetchWithTimeout } from '@/lib/fetch-with-timeout';
import type { SeatEventType } from '@/lib/types';
import { Skeleton } from '@/components/ui/skeleton';

export interface ProfileActivityApiEvent {
  id: string;
  eventType: SeatEventType | string;
  poolId: string;
  /** Human-readable grid display name resolved server-side
   *  (e.g. "BANKR", "Boardroom"). `null` when no Grid record could
   *  be resolved — the row falls back to a truncated `poolId`. */
  gridName?: string | null;
  seatId: string;
  timestamp: number;
  blockNumber: number;
  txHash: string;
  price?: string | null;
  amount?: string | null;
  newPrice?: string | null;
  newBalance?: string | null;
  oldHolder?: string | null;
  newHolder?: string | null;
  refund?: string | null;
}

function timeAgo(tsSec: number): string {
  const s = Math.max(0, Math.floor(Date.now() / 1000 - tsSec));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function seatIndexFromSeatId(seatId: string): string | undefined {
  if (!seatId) return undefined;
  const parts = seatId.split('-');
  const last = parts[parts.length - 1];
  return /^\d+$/.test(last) ? last.padStart(3, '0') : undefined;
}

type ActivityKind =
  | 'fee'
  | 'claim'
  | 'buy'
  | 'reprice'
  | 'deposit'
  | 'withdraw'
  | 'launch'
  | 'tax'
  | 'other';

const META: Record<
  ActivityKind,
  { label: string; icon: React.ReactNode; tone: string }
> = {
  fee: {
    label: 'Fee claim',
    icon: <Gem className="h-3 w-3" />,
    tone: 'border-emerald-400/40 bg-emerald-500/10 text-emerald-200',
  },
  claim: {
    label: 'Vacant claim',
    icon: <Sparkles className="h-3 w-3" />,
    tone: 'border-sky-400/40 bg-sky-500/10 text-sky-200',
  },
  buy: {
    label: 'Buyout',
    icon: <Swords className="h-3 w-3" />,
    tone: 'border-rose-400/40 bg-rose-500/10 text-rose-200',
  },
  reprice: {
    label: 'Reprice',
    icon: <Shuffle className="h-3 w-3" />,
    tone: 'border-amber-400/40 bg-amber-500/10 text-amber-200',
  },
  deposit: {
    label: 'Deposit',
    icon: <Wallet className="h-3 w-3" />,
    tone: 'border-emerald-400/30 bg-emerald-500/5 text-emerald-200',
  },
  withdraw: {
    label: 'Withdraw',
    icon: <Receipt className="h-3 w-3" />,
    tone: 'border-orange-400/40 bg-orange-500/10 text-orange-200',
  },
  launch: {
    label: 'Launch',
    icon: <Hammer className="h-3 w-3" />,
    tone: 'border-violet-400/40 bg-violet-500/10 text-violet-200',
  },
  tax: {
    label: 'Tax',
    icon: <Receipt className="h-3 w-3" />,
    tone: 'border-zinc-400/40 bg-zinc-500/10 text-zinc-200',
  },
  other: {
    label: 'Event',
    icon: <Sparkles className="h-3 w-3" />,
    tone: 'border-white/20 bg-white/5 text-zinc-200',
  },
};

function classify(type: string): { kind: ActivityKind; action: string } {
  switch (type) {
    case 'FEES_CLAIMED':
      return { kind: 'fee', action: 'claimed fees' };
    case 'CREATOR_FEES_WITHDRAWN':
      return { kind: 'tax', action: 'claimed taxes' };
    case 'CLAIMED':
      return { kind: 'claim', action: 'claimed a vacant tile' };
    case 'BOUGHT':
      return { kind: 'buy', action: 'bought a seat' };
    case 'PRICE_UPDATED':
      return { kind: 'reprice', action: 'updated price' };
    case 'DEPOSIT_ADDED':
      return { kind: 'deposit', action: 'added deposit' };
    case 'BALANCE_WITHDRAWN':
      return { kind: 'withdraw', action: 'withdrew balance' };
    case 'ABANDONED':
    case 'FORFEITED':
      return { kind: 'other', action: type === 'ABANDONED' ? 'abandoned seat' : 'forfeited seat' };
    case 'TAX_POKED':
      return { kind: 'tax', action: 'tax poke' };
    default:
      return { kind: 'other', action: type.toLowerCase().replace(/_/g, ' ') };
  }
}

/**
 * Page size for the activity infinite scroll. 20 rows is small enough that
 * even a stale-cached page renders in one frame, and the API's per-side
 * over-fetch (`limit*2`) keeps merged-pagination cheap on the indexer.
 */
const PAGE_SIZE = 20;

interface ActivityPage {
  events: ProfileActivityApiEvent[];
  /** Server-emitted next cursor (oldest event's `timestamp`); `null` when
   *  the API knows there are no more pages. */
  nextCursor: number | null;
}

export const PROFILE_ACTIVITY_QUERY_KEY = (addr: string) =>
  ['profile-activity', addr.toLowerCase()] as const;

export async function fetchProfileActivityPage(
  address: string,
  cursor?: number,
): Promise<ActivityPage> {
  const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
  if (typeof cursor === 'number' && cursor > 0) params.set('before', String(cursor));
  const res = await fetchWithTimeout(
    `/api/users/${encodeURIComponent(address.toLowerCase())}/activity?${params}`,
    { timeoutMs: 45_000 },
  );
  if (!res.ok) throw new Error('activity fetch failed');
  const json = (await res.json()) as {
    data?: { events?: ProfileActivityApiEvent[] };
    meta?: { nextCursor?: number | null };
  };
  return {
    events: json?.data?.events ?? [],
    nextCursor: json?.meta?.nextCursor ?? null,
  };
}

function ProfileActivityFeedInner({
  address,
  viewerIsSubject,
}: {
  address: string;
  viewerIsSubject: boolean;
}) {
  const q = useInfiniteQuery({
    queryKey: PROFILE_ACTIVITY_QUERY_KEY(address),
    queryFn: ({ pageParam }) => fetchProfileActivityPage(address, pageParam),
    initialPageParam: undefined as number | undefined,
    // Trust the server's `nextCursor`. Falling back to "derive from the
    // last event's timestamp" would risk an infinite loop on a perfectly-
    // sized terminal page (events.length === PAGE_SIZE but no more rows
    // exist on the indexer): the server returns `null` in that situation.
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!address,
    staleTime: 20_000,
    retry: 1,
  });

  // Flatten + dedupe by event id. Timestamps are second-resolution so two
  // events at the same second can theoretically straddle a page boundary
  // when the cursor uses `_lt`; the dedupe absorbs that edge case without
  // needing composite cursors.
  const events = useMemo<ProfileActivityApiEvent[]>(() => {
    if (!q.data?.pages) return [];
    const seen = new Set<string>();
    const out: ProfileActivityApiEvent[] = [];
    for (const page of q.data.pages) {
      for (const ev of page.events) {
        if (seen.has(ev.id)) continue;
        seen.add(ev.id);
        out.push(ev);
      }
    }
    return out;
  }, [q.data]);

  // Sentinel-based infinite scroll. We attach an IntersectionObserver to a
  // 1px div at the bottom of the list; once it scrolls into view (with a
  // generous rootMargin so we start fetching before the user actually
  // sees "the end"), the next page kicks off. Mirrors the pattern in
  // `components/grid-v2/event-feed.tsx`.
  const sentinelRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    if (!q.hasNextPage || q.isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) q.fetchNextPage();
      },
      { rootMargin: '160px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [q.hasNextPage, q.isFetchingNextPage, q.fetchNextPage]);

  if (q.isPending) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <Skeleton
            key={i}
            className="h-12 w-full rounded-[4px] border border-white/[0.04] bg-black/30"
          />
        ))}
      </div>
    );
  }

  if (q.isError) {
    return (
      <p className="text-[11px] text-zinc-500 font-mono">
        Could not load activity.
      </p>
    );
  }

  if (events.length === 0) {
    return (
      <p className="text-[11px] text-zinc-500 font-mono py-4 text-center">
        No onchain events yet.
      </p>
    );
  }

  return (
    <ul className="flex flex-col divide-y divide-white/[0.04] rounded-[4px] border border-white/[0.04] bg-black/30 flex-1 min-h-0 overflow-y-auto">
      {events.map((e) => (
        <ActivityRow
          key={e.id}
          event={e}
          viewerIsSubject={viewerIsSubject}
        />
      ))}
      {q.hasNextPage && (
        <li
          ref={sentinelRef}
          aria-hidden
          className="flex items-center justify-center py-3 text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-600"
        >
          {q.isFetchingNextPage ? (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading more
            </span>
          ) : (
            <span className="opacity-60">Scroll for more</span>
          )}
        </li>
      )}
      {!q.hasNextPage && events.length > PAGE_SIZE && (
        <li className="py-3 text-center text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-600">
          End of history
        </li>
      )}
    </ul>
  );
}

function ActivityRow({
  event,
  viewerIsSubject,
}: {
  event: ProfileActivityApiEvent;
  viewerIsSubject: boolean;
}) {
  const { kind, action } = classify(event.eventType);
  const meta = META[kind];
  const seatCode = seatIndexFromSeatId(event.seatId);
  const sub = `${meta.label} · ${timeAgo(event.timestamp)} ago`;

  // Prefer the server-resolved display name (e.g. "BANKR", "Boardroom");
  // fall back to a truncated pool id only when the activity API couldn't
  // resolve a Grid record for this event.
  const shortPool = `${event.poolId.slice(0, 6)}…${event.poolId.slice(-4)}`;
  const gridLabel = event.gridName?.trim() || shortPool;
  const usingFallback = gridLabel === shortPool;

  return (
    <li className="flex items-center gap-2 px-2.5 py-1.5 text-[11px]">
      <span
        className={cn(
          'w-6 h-6 rounded-[3px] grid place-items-center border shrink-0',
          meta.tone
        )}
        aria-hidden
      >
        {meta.icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-zinc-200 truncate">
          {viewerIsSubject ? (
            <span className="text-zinc-400">You </span>
          ) : (
            <span className="text-zinc-400">They </span>
          )}
          {action}
          {seatCode && (
            <span className="ml-1 font-mono text-amber-100">#{seatCode}</span>
          )}
          <span className="text-zinc-500"> on </span>
          <span
            className={cn(
              'text-amber-100/90',
              usingFallback ? 'font-mono text-[10px]' : 'font-medium',
            )}
            title={event.poolId}
          >
            {gridLabel}
          </span>
        </div>
        <div className="text-[9px] font-mono tracking-[0.18em] uppercase text-zinc-500">
          {sub}
        </div>
      </div>
    </li>
  );
}

export function ProfileActivityFeed(props: {
  address: string;
  viewerIsSubject: boolean;
}) {
  return <ProfileActivityFeedInner {...props} />;
}
